import { X, Mail, KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/Input';

interface PasswordModalProps {
    show: boolean;
    onClose: () => void;
    step: 'request' | 'reset';
    onRequestCode: () => void;
    onSubmit: (e: React.FormEvent) => void;
    loading: boolean;
    messages: { type: string; text: string };
    form: { newPassword: string; confirmPassword: string; code: string };
    setForm: (form: any) => void;
    userEmail: string;
}

export function PasswordModal({
    show,
    onClose,
    step,
    onRequestCode,
    onSubmit,
    loading,
    messages,
    form,
    setForm,
    userEmail,
}: PasswordModalProps) {
    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-neutral-900 w-full max-w-md rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white">Alterar Senha</h3>
                    <button onClick={onClose} className="text-white/40 hover:text-white">
                        <X size={20} />
                    </button>
                </div>

                {step === 'request' ? (
                    <div className="p-6 space-y-5">
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                            <Mail size={20} className="text-orange-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-white/80">
                                    Enviaremos um código de 6 dígitos para:
                                </p>
                                <p className="text-sm font-semibold text-white mt-1">{userEmail}</p>
                            </div>
                        </div>

                        {messages.text && (
                            <div className={`text-sm p-3 rounded-lg ${messages.type === 'error' ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
                                {messages.text}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-white rounded-lg font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={onRequestCode}
                                disabled={loading}
                                className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-bold transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Enviando...' : 'Enviar Código'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="p-6 space-y-4">
                        <div className="flex items-center gap-2 text-sm text-white/50 mb-1">
                            <KeyRound size={14} className="text-orange-500" />
                            <span>Código enviado para <span className="text-white/70">{userEmail}</span></span>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-white/40 uppercase mb-2">Código de verificação</label>
                            <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={form.code}
                                onChange={e => setForm({ ...form, code: e.target.value.replace(/\D/g, '') })}
                                className="py-3 text-center text-lg font-mono tracking-widest"
                                placeholder="000000"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-white/40 uppercase mb-2">Nova Senha</label>
                            <Input
                                type="password"
                                value={form.newPassword}
                                onChange={e => setForm({ ...form, newPassword: e.target.value })}
                                className="py-3"
                                placeholder="Mínimo 6 caracteres"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-white/40 uppercase mb-2">Confirmar Nova Senha</label>
                            <Input
                                type="password"
                                value={form.confirmPassword}
                                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                                className="py-3"
                                placeholder="Confirme a senha"
                            />
                        </div>

                        {messages.text && (
                            <div className={`text-sm p-3 rounded-lg ${messages.type === 'error' ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
                                {messages.text}
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-white rounded-lg font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-bold transition-colors disabled:opacity-50"
                            >
                                {loading ? 'Alterando...' : 'Confirmar'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
